import React, {
  createContext,
  useContext,
  useState,
  ReactNode,
  useEffect,
} from "react";
import { TimeSlot } from "../types/booking";

interface TimezoneConversionDebug {
  original: { start: Date; end: Date; timeZone: string };
  host: { start: Date; end: Date; timeZone: string };
  booking: { start: Date; end: Date; timeZone: string };
  conversionPath: string[];
}

interface BookingDebugContextType {
  isDebugModeEnabled: boolean;
  isDebugPanelVisible: boolean;
  selectedDebugSlot: TimeSlot | null;
  showDebugModal: boolean;
  conversionDetails: TimezoneConversionDebug | null;

  // Methods
  setSelectedDebugSlot: (slot: TimeSlot | null) => void;
  toggleDebugMode: () => void;
  toggleDebugPanelVisibility: () => void;
  setShowDebugModal: (show: boolean) => void;
  setConversionDetails: (details: TimezoneConversionDebug | null) => void;
  formatTimeForDebug: (date: Date, timeZone: string) => string;
}

const BookingDebugContext = createContext<BookingDebugContextType | undefined>(
  undefined
);

interface BookingDebugProviderProps {
  children: ReactNode;
}

export const BookingDebugProvider: React.FC<BookingDebugProviderProps> = ({
  children,
}) => {
  // Check if debug mode is enabled from localStorage
  const [isDebugModeEnabled, setIsDebugModeEnabled] = useState<boolean>(
    localStorage.getItem("DEBUG_CALENDAR") === "true"
  );
  // Always visible by default, separate from functionality
  const [isDebugPanelVisible, setIsDebugPanelVisible] = useState<boolean>(true);
  const [selectedDebugSlot, setSelectedDebugSlot] = useState<TimeSlot | null>(
    null
  );
  const [showDebugModal, setShowDebugModal] = useState<boolean>(false);
  const [conversionDetails, setConversionDetails] =
    useState<TimezoneConversionDebug | null>(null);

  // Check for debug mode flag changes in localStorage
  useEffect(() => {
    const handleStorageChange = () => {
      const newDebugMode = localStorage.getItem("DEBUG_CALENDAR") === "true";
      setIsDebugModeEnabled(newDebugMode);
    };

    window.addEventListener("storage", handleStorageChange);
    return () => window.removeEventListener("storage", handleStorageChange);
  }, []);

  const toggleDebugMode = () => {
    const newValue = !isDebugModeEnabled;
    localStorage.setItem("DEBUG_CALENDAR", newValue.toString());
    setIsDebugModeEnabled(newValue);
  };

  const toggleDebugPanelVisibility = () => {
    setIsDebugPanelVisible(!isDebugPanelVisible);
  };

  // Format time values for display in debug UI
  const formatTimeForDebug = (date: Date, timeZone: string): string => {
    // Display the time in the requested timezone for debugging
    try {
      return (
        new Date(date).toLocaleTimeString("en-US", {
          hour: "2-digit",
          minute: "2-digit",
          timeZone: timeZone,
          hour12: true,
        }) +
        " " +
        timeZone
      );
    } catch (error) {
      // If timezone formatting fails, fall back to ISO string
      return date.toISOString();
    }
  };

  const value = {
    isDebugModeEnabled,
    isDebugPanelVisible,
    selectedDebugSlot,
    showDebugModal,
    conversionDetails,
    setSelectedDebugSlot,
    toggleDebugMode,
    toggleDebugPanelVisibility,
    setShowDebugModal,
    setConversionDetails,
    formatTimeForDebug,
  };

  return (
    <BookingDebugContext.Provider value={value}>
      {children}
    </BookingDebugContext.Provider>
  );
};

export const useBookingDebug = () => {
  const context = useContext(BookingDebugContext);
  if (context === undefined) {
    throw new Error(
      "useBookingDebug must be used within a BookingDebugProvider"
    );
  }
  return context;
};
